/**
 * Décision 374 — les gestes de la chargée sur un contrat déjà souscrit :
 * corriger sa durée, le supprimer s'il a été créé par erreur, le clore.
 * Chaque geste porte un motif : il va au journal, avec l'avant et l'après.
 */
import { z } from 'zod';
import { DUREE_CONTRAT_MOIS_MAX, LONGUEUR_MOTIF_MIN } from '../rules/contrats.js';
import { POPULATIONS_COUVERTES, PRECISION_POPULATION_MAX, refusPopulationCouverte, } from '../rules/population-couverte.js';
/** `AAAA-MM-JJ`. */
const dateCivile = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ');
/** Pourquoi : relu au journal par la Direction ; jamais une donnée de salarié. */
export const schemaMotifContrat = z
    .string()
    .trim()
    .min(LONGUEUR_MOTIF_MIN, `Le motif est obligatoire (${String(LONGUEUR_MOTIF_MIN)} caractères au moins)`)
    .max(500);
/**
 * La nouvelle durée : un nombre de mois (la date de fin s'en déduit, comme à
 * la souscription), une date de fin (la durée s'en déduit si elle tombe juste),
 * ou pas de terme du tout.
 */
export const schemaDureeContrat = z.discriminatedUnion('terme', [
    z
        .object({
        terme: z.literal('DUREE'),
        dureeMois: z.number().int().min(1).max(DUREE_CONTRAT_MOIS_MAX),
        motif: schemaMotifContrat,
    })
        .strict(),
    z
        .object({ terme: z.literal('DATE_FIN'), dateFin: dateCivile, motif: schemaMotifContrat })
        .strict(),
    z.object({ terme: z.literal('SANS_FIN'), motif: schemaMotifContrat }).strict(),
]);
/**
 * S1428 — la population couverte d'un contrat, renseignée ou corrigée après
 * coup. Informative seulement ; le motif va au journal, comme les autres gestes.
 */
export const schemaPopulationContrat = z
    .object({
    populationCouverte: z.enum(POPULATIONS_COUVERTES),
    populationPrecision: z.string().trim().max(PRECISION_POPULATION_MAX).nullable(),
    motif: schemaMotifContrat,
})
    .strict()
    .superRefine((d, ctx) => {
    const refus = refusPopulationCouverte(d.populationCouverte, d.populationPrecision);
    if (refus !== null)
        ctx.addIssue({ code: 'custom', message: refus, path: ['populationPrecision'] });
});
/** Supprimer un contrat créé par erreur : un motif, rien d'autre. */
export const schemaSuppressionContrat = z.object({ motif: schemaMotifContrat }).strict();
/** Clore un contrat : le jour de la clôture et le motif. */
export const schemaClotureContrat = z
    .object({ dateCloture: dateCivile, motif: schemaMotifContrat })
    .strict();
// ── Décision 374 (e) — déplacer les salariés d'un contrat (le changement d'un
// salarié passe par « Changer l'offre ou le contrat », décisions 385 et 397) ──
/** Tous les salariés d'un contrat, avec leurs ayants droit, vers un autre. */
export const schemaDeplacementSalaries = z
    .object({ versContratId: z.uuid(), motif: schemaMotifContrat })
    .strict();
export const schemaCibleDeplacement = z.object({ vers: z.uuid() });
/** Ce que le déplacement fera, lu avant de valider. */
export const schemaApercuDeplacement = z.object({
    salaries: z.number().int(),
    ayantsDroit: z.number().int(),
    /** Les matricules déjà portés sur le contrat d'arrivée (casse ignorée) : ils bloquent. */
    matriculesEnConflit: z.array(z.string()),
});
// ── Décisions 384 à 386 — changer l'offre d'un salarié, c'est changer de contrat ──
/**
 * CP-08 : « tout le contrat » passerait sur une offre qu'un autre contrat en
 * cours porte déjà (même population). Le 409 dit lequel (`contratExistantId`) :
 * l'écran propose d'y faire passer le patient seul.
 */
export const RAISON_CONTRAT_EXISTANT_SUR_L_OFFRE = 'CONTRAT_EXISTANT_SUR_L_OFFRE';
/** Le libellé de population d'un contrat que la plateforme crée elle-même. */
export const POPULATION_CREE_AU_CHANGEMENT_D_OFFRE = 'Créé au changement d’offre';
/**
 * « Changer l'offre » : rejoindre un contrat en cours de l'entreprise
 * (`contratId`), ou prendre une offre qu'elle n'a pas encore sous contrat
 * (`offreId`) — la plateforme crée alors le contrat.
 */
export const schemaChangementOffre = z
    .object({
    contratId: z.uuid().optional(),
    offreId: z.uuid().optional(),
    motif: schemaMotifContrat,
})
    .strict()
    .refine((d) => (d.contratId === undefined) !== (d.offreId === undefined), {
    message: 'Choisissez un contrat en cours ou une autre offre, l’un des deux.',
    path: ['contratId'],
});
/** Le contrat d'arrivée, et s'il vient d'être créé. */
export const schemaResultatChangementOffre = z.object({
    contratId: z.uuid(),
    contratCree: z.boolean(),
});
//# sourceMappingURL=contrats.js.map